import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check active session
        supabase.auth.getSession().then(({ data: { session } }) => {
            setUser(session?.user ?? null);
            setLoading(false);
        });

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            setUser(session?.user ?? null);
            // Clear Pro status whenever any session ends so the next user
            // on this device doesn't inherit a previous account's subscription
            // Only clear Pro status on explicit user-triggered sign-out,
            // NOT on automatic token expiry/refresh — that would silently strip
            // paying customers of their premium status.
            if (event === 'SIGNED_OUT' && session === null && localStorage.getItem('userSigningOut') === 'true') {
                localStorage.removeItem('isPro');
                localStorage.removeItem('proSource');
                localStorage.removeItem('promoCode');
                localStorage.removeItem('userSigningOut');
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const signUp = async (email, password) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
        });
        return { data, error };
    };

    const signIn = async (email, password) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });
        return { data, error };
    };

    const signOut = async () => {
        // Mark this as an intentional sign-out so the auth listener knows
        // it's safe to clear isPro (vs. an automatic session expiry)
        localStorage.setItem('userSigningOut', 'true');
        const { error } = await supabase.auth.signOut();
        if (error) {
            // If sign out failed, remove the flag so isPro isn't cleared
            localStorage.removeItem('userSigningOut');
        }
        return { error };
    };

    const value = {
        user,
        loading,
        signUp,
        signIn,
        signOut,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
